import React, { useContext, useState } from 'react';
import { getURI } from '../../api/apiConfig';
import { UserContext } from '../../context/UserContext';

// @mui/material components
import Alert from '@mui/material/Alert';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import { makeStyles } from '@mui/styles';

// @mui/icons-material
import Email from '@mui/icons-material/Email';

// core components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';
import CardHeader from 'components/Card/CardHeader.js';
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import styles from 'assets/jss/material-dashboard-pro-react/views/loginPageStyle.js';
import { generateCorrelationId } from '@src/helpers/generateCorrelationId';

const useStyles = makeStyles(styles);

export default function Login() {
  const [cardAnimaton, setCardAnimation] = useState('cardHidden');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userContext, setUserContext] = useContext(UserContext);

  React.useEffect(() => {
    let id = setTimeout(function () {
      setCardAnimation('');
    }, 700);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.clearTimeout(id);
    };
  });
  const classes = useStyles();

  const [openError, setOpenError] = useState(false);

  const setError = (message) => {
    setErrorMessage(message);
    setOpenError(true);
  };

  const handleErrorClose = () => {
    setOpenError(false);
    setErrorMessage('');
  };

  const formSubmitHandler = () => {
    const genericErrorMessage = 'Something went wrong! Please try again later.';
    const headers = new Headers();
    headers.append('Content-Type', 'application/json')
    headers.append('X-CORRELATION-ID', generateCorrelationId());
    fetch(getURI('login'), {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ username: email, password: password }),
    })
      .then(async (response) => {
        setIsSubmitting(false);
        if (!response.ok) {
          if (response.status === 400) {
            setError('Please fill all the fields correctly!');
          } else if (response.status === 401) {
            setError('Invalid email and password combination.');
          } else {
            setError(genericErrorMessage);
          }
        } else {
          const data = await response.json();
          setUserContext((oldValues) => {
            return { ...oldValues, token: data.token };
          });
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        setError(genericErrorMessage);
      });
  };

  return (
    <div className={classes.container}>
      <GridContainer justifyContent='center'>
        <GridItem xs={12} sm={6} md={4}>
          <Card login className={classes[cardAnimaton]}>
            <CardHeader
              className={`${classes.cardHeader} ${classes.textCenter}`}
              color='primary'
            >
              <h4 className={classes.cardTitle}>Enter Credentials</h4>
            </CardHeader>
            <CardBody>
              <CustomInput
                labelText='Email'
                id='email'
                value={email}
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  onChange: (e) => setEmail(e.target.value),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Email className={classes.inputAdornmentIcon} />
                    </InputAdornment>
                  ),
                }}
              />
              <CustomInput
                labelText='Password'
                id='password'
                value={password}
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  onChange: (e) => setPassword(e.target.value),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Icon className={classes.inputAdornmentIcon}>
                        lock_outline
                      </Icon>
                    </InputAdornment>
                  ),
                  type: 'password',
                  autoComplete: 'off',
                }}
              />
            </CardBody>
            <CardFooter className={classes.justifyContentCenter}>
              <Button
                color='info'
                size='lg'
                block
                disabled={isSubmitting}
                onClick={formSubmitHandler}
                text={`$`}
              >
                {isSubmitting ? 'Signing In' : 'Sign In'}
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
      <Snackbar
        open={openError}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleErrorClose} severity='error' variant='filled'>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
